import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SearchBox from '@/components/feature/SearchBox';
import MovieCard from '@/components/feature/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import Breadcrumb from '@/components/base/Breadcrumb';
import { searchMovies } from '@/lib/catalog';

const ALL = '全部';

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get('q') || '').trim();

  const [typeFilter, setTypeFilter] = useState(ALL);
  const [yearFilter, setYearFilter] = useState(ALL);

  useEffect(() => {
    setTypeFilter(ALL);
    setYearFilter(ALL);
  }, [keyword]);

  const matched = useMemo(() => searchMovies(keyword), [keyword]);

  const years = useMemo(
    () => [ALL, ...Array.from(new Set(matched.map((m) => String(m.year)))).sort((a, b) => b.localeCompare(a))],
    [matched],
  );

  const results = useMemo(() => {
    let list = matched;
    if (typeFilter === '电影') list = list.filter((m) => m.type === 'movie');
    if (typeFilter === '电视剧') list = list.filter((m) => m.type === 'tv');
    if (yearFilter !== ALL) list = list.filter((m) => String(m.year) === yearFilter);
    return list;
  }, [matched, typeFilter, yearFilter]);

  const typeOptions = [ALL, '电影', '电视剧'];

  return (
    <div className="w-full">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-10">
          <Breadcrumb items={[{ label: '影视搜索' }]} />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            影视搜索
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            支持按片名、演员、导演、类型与关键词搜索电影和电视剧。
          </p>
          <div className="mt-5 max-w-2xl">
            <SearchBox key={keyword} initialValue={keyword} size="lg" placeholder="输入关键词，例如：科幻、陈默、长安…" />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-8">
        {keyword ? (
          <>
            <div className="flex flex-col gap-4 border-b border-background-200 pb-5 md:flex-row md:items-center md:justify-between">
              <p className="font-label text-sm text-foreground-600">
                关键词「<span className="font-semibold text-foreground-950">{keyword}</span>」
                共找到 <span className="font-semibold text-accent-600">{results.length}</span> 条结果
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {typeOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setTypeFilter(option)}
                    className={`whitespace-nowrap rounded-full px-3 py-1 font-label text-xs transition-colors cursor-pointer ${
                      option === typeFilter
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-secondary-100'
                    }`}
                  >
                    {option}
                  </button>
                ))}
                <select
                  value={yearFilter}
                  onChange={(event) => setYearFilter(event.target.value)}
                  aria-label="按年份筛选"
                  className="rounded-full border border-background-300 bg-background-50 px-3 py-1 font-label text-xs text-foreground-800 outline-none cursor-pointer"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year === ALL ? '全部年份' : `${year} 年`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {results.length > 0 ? (
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {results.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="mt-8">
                <EmptyState
                  icon="ri-search-eye-line"
                  title="没有找到相关影视作品"
                  description="换个关键词试试，比如影片类型、演员姓名，或直接浏览分类页。"
                  action={
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <Link
                        to="/"
                        className="rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                      >
                        返回首页
                      </Link>
                      <Link
                        to="/category/movie"
                        className="rounded-md border border-background-300 px-5 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:bg-secondary-100 cursor-pointer"
                      >
                        浏览电影分类
                      </Link>
                    </div>
                  }
                />
              </div>
            )}
          </>
        ) : (
          <EmptyState
            icon="ri-search-line"
            title="输入关键词开始搜索"
            description="试试搜索片名、演员姓名或类型关键词，例如「科幻」「悬疑」「苏念」。"
            action={
              <Link
                to="/"
                className="rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                返回首页
              </Link>
            }
          />
        )}

        <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-100 p-4 text-center">
          <p className="text-xs leading-relaxed text-foreground-600">
            <i className="ri-information-line mr-1" aria-hidden="true" />
            搜索结果为前端关键词匹配的演示数据，不代表真实片库内容。
          </p>
        </div>
      </div>
    </div>
  );
}